import axios from "axios";
import { Base_URL } from "../url";

// Create milestone
export const createMilestone = async (milestoneData, userName) => {
  try {
    const response = await axios.post(
      `${Base_URL}/MilestoneMaster/CreateMilestone?UserName=${userName}`,
      milestoneData,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to create milestone",
    );
  }
};

// Get all milestones (with optional includeInactive parameter)
export const getAllMilestones = async (includeInactive = false) => {
  try {
    const response = await axios.get(
      `${Base_URL}/MilestoneMaster/GetAllMilestones`,
      {
        params: {
          includeInactive: includeInactive,
        },
      },
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to fetch milestones",
    );
  }
};

// Get milestone by ID
export const getMilestoneById = async (milestoneMavroId) => {
  try {
    const response = await axios.get(
      `${Base_URL}/MilestoneMaster/GetMilestoneById/${milestoneMavroId}`,
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to fetch milestone",
    );
  }
};

// Get milestones by project ID (with optional includeInactive parameter)
export const getMilestonesByProzmaPrId = async (
  prozmaPrId,
  includeInactive = false,
) => {
  try {
    const response = await axios.get(
      `${Base_URL}/MilestoneMaster/GetMilestonesByProzmaPrId/${prozmaPrId}`,
      {
        params: {
          includeInactive: includeInactive,
        },
      },
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to fetch milestones for project",
    );
  }
};

// Get milestones by organization ID (with optional includeInactive parameter)
export const getMilestonesByOrgId = async (orgId, includeInactive = false) => {
  try {
    const response = await axios.get(
      `${Base_URL}/MilestoneMaster/GetMilestonesByOrgId/${orgId}`,
      {
        params: {
          includeInactive: includeInactive,
        },
      },
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
        "Failed to fetch milestones for organization",
    );
  }
};

// Get milestones by both project and organization ID
// includeInactive: false (default) - returns only active milestones
// includeInactive: true - returns both active and inactive milestones
export const getMilestonesByProjectAndOrg = async (
  prozmaPrId,
  orgId,
  includeInactive = false,
) => {
  try {
    const response = await axios.get(
      `${Base_URL}/MilestoneMaster/GetMilestonesByProjectAndOrg`,
      {
        params: {
          prozmaPrId,
          orgId,
          includeInactive: includeInactive,
        },
      },
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
        "Failed to fetch milestones for project and organization",
    );
  }
};

// Update milestone
export const updateMilestone = async (milestoneData, userName) => {
  try {
    const response = await axios.put(
      `${Base_URL}/MilestoneMaster/UpdateMilestone?UserName=${userName}`,
      milestoneData,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to update milestone",
    );
  }
};

// Soft delete milestone (deactivate)
export const softDeleteMilestone = async (milestoneMavroId, userName) => {
  try {
    const response = await axios.delete(
      `${Base_URL}/MilestoneMaster/SoftDeleteMilestone/${milestoneMavroId}`,
      {
        params: {
          UserName: userName,
        },
      },
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to soft delete milestone",
    );
  }
};

// Restore milestone (reactivate)
export const restoreMilestone = async (milestoneMavroId, userName) => {
  try {
    const response = await axios.post(
      `${Base_URL}/MilestoneMaster/RestoreMilestone/${milestoneMavroId}`,
      null,
      {
        params: {
          UserName: userName,
        },
      },
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to restore milestone",
    );
  }
};

// Permanent delete milestone (hard delete)
export const permanentDeleteMilestone = async (milestoneMavroId) => {
  try {
    const response = await axios.delete(
      `${Base_URL}/MilestoneMaster/PermanentDeleteMilestone/${milestoneMavroId}`,
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to permanently delete milestone",
    );
  }
};

// Get all active milestones (not soft deleted)
export const getAllActiveMilestones = async () => {
  try {
    const response = await axios.get(
      `${Base_URL}/MilestoneMaster/GetAllActiveMilestones`,
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to fetch active milestones",
    );
  }
};

// Get all deleted (inactive) milestones
export const getDeletedMilestones = async () => {
  try {
    const response = await axios.get(
      `${Base_URL}/MilestoneMaster/GetDeletedMilestones`,
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to fetch deleted milestones",
    );
  }
};

// Download milestones Excel (with optional includeInactive parameter)
export const downloadMilestonesExcel = async (includeInactive = false) => {
  try {
    const response = await axios.post(
      `${Base_URL}/MilestoneMaster/DownloadMilestonesExcel`,
      {},
      {
        params: {
          includeInactive: includeInactive,
        },
        responseType: "blob", // Important for file download
      },
    );
    return response;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to download milestones Excel",
    );
  }
};

// Search milestones with multiple parameters
export const searchMilestones = async (searchParams = {}) => {
  try {
    const response = await axios.get(
      `${Base_URL}/MilestoneMaster/SearchMilestones`,
      {
        params: searchParams,
      },
    );
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to search milestones",
    );
  }
};

//
// =============================================
// MILESTONE TYPES
// =============================================

export const getMilestoneTypes = async () => {
  try {
    const response = await axios.get(`${Base_URL}/Milestone/types`);

    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to fetch milestone types",
    );
  }
};

//
// =============================================
// MILESTONE STATUSES
// =============================================

export const getMilestoneStatuses = async () => {
  try {
    const response = await axios.get(`${Base_URL}/Milestone/statuses`);

    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to fetch milestone statuses",
    );
  }
};

//
// =============================================
// MILESTONE TYPES - NEW APIs
// =============================================

// Get milestone types by OrgId
export const getMilestoneTypesByOrg = async (orgId) => {
  try {
    const response = await axios.get(
      `${Base_URL}/Milestone/types/by-org/${orgId}`,
    );

    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
        "Failed to fetch milestone types by organization",
    );
  }
};

// Get milestone type by GUID
export const getMilestoneTypeByGuid = async (guid) => {
  try {
    const response = await axios.get(
      `${Base_URL}/Milestone/type/by-guid/${guid}`,
    );

    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message || "Failed to fetch milestone type by GUID",
    );
  }
};

//
// =============================================
// MILESTONE STATUSES - NEW APIs
// =============================================

// Get milestone statuses by OrgId
export const getMilestoneStatusesByOrg = async (orgId) => {
  try {
    const response = await axios.get(
      `${Base_URL}/Milestone/statuses/by-org/${orgId}`,
    );

    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
        "Failed to fetch milestone statuses by organization",
    );
  }
};

// Get milestone status by GUID
export const getMilestoneStatusByGuid = async (guid) => {
  try {
    const response = await axios.get(
      `${Base_URL}/Milestone/status/by-guid/${guid}`,
    );

    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
        "Failed to fetch milestone status by GUID",
    );
  }
};
